import React, { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Switch,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { ResellerProfile } from '../../types/b2b';

interface ResellerListProps {
  onCreateClick?: () => void;
  onResellerClick?: (reseller: ResellerProfile) => void;
  hasB2BAccess?: boolean;
}

const fetchResellers = async () => {
  const response = await axiosInstance.get('/reseller-profiles');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  {
    field: 'isApproved',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Suspended' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'companyName', headerName: 'Company' },
  {
    field: 'user',
    headerName: 'Contact',
    valueFormatter: (_, row) => row.user ? `${row.user.firstName} ${row.user.lastName}` : 'N/A',
  },
  {
    field: 'email',
    headerName: 'Email',
    valueFormatter: (_, row) => row.user?.email || 'N/A',
  },
  {
    field: 'tier',
    headerName: 'Pricing Tier',
    valueFormatter: (_, row) => row.defaultTier?.tierName || 'None',
  },
  {
    field: 'isApproved',
    headerName: 'Status',
    valueFormatter: (value) => value ? 'Active' : 'Suspended',
  },
];

export const ResellerList = ({ onCreateClick, onResellerClick, hasB2BAccess = true }: ResellerListProps = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data: resellers, isLoading } = useQuery({
    queryKey: ['reseller-profiles'],
    queryFn: fetchResellers,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/reseller-profiles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reseller-profiles'] });
      showMessage('Reseller access revoked', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to revoke access', 'error');
    },
  });

  const toggleApprovedMutation = useMutation({
    mutationFn: ({ id, isApproved }: { id: string; isApproved: boolean }) =>
      axiosInstance.patch(`/reseller-profiles/${id}`, { isApproved }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reseller-profiles'] });
      showMessage('Reseller status updated', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update status', 'error');
    },
  });

  const filteredData = useMemo(() => {
    if (!resellers) return [];

    return resellers.filter((item: ResellerProfile) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const userName = item.user ? `${item.user.firstName} ${item.user.lastName}` : '';
        const email = item.user?.email || '';
        const matchesSearch =
          item.companyName.toLowerCase().includes(search) ||
          userName.toLowerCase().includes(search) ||
          email.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (activeFilters.isApproved !== undefined && activeFilters.isApproved !== '') {
        const isApprovedFilter = activeFilters.isApproved === 'true';
        if (item.isApproved !== isApprovedFilter) return false;
      }

      return true;
    });
  }, [resellers, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to revoke B2B access for this reseller?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleApproved = (id: string, currentStatus: boolean) => {
    toggleApprovedMutation.mutate({ id, isApproved: !currentStatus });
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = resellers?.length || 0;
    const active = resellers?.filter((r: ResellerProfile) => r.isApproved).length || 0;
    const suspended = total - active;

    return { total, active, suspended };
  }, [resellers]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <GroupIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B PORTAL
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              RESELLERS
            </Typography>
          </Box>
          {hasB2BAccess && onCreateClick && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateClick}
              sx={{
                bgcolor: palette.primary,
                color: '#fff',
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                '&:hover': {
                  bgcolor: alpha(palette.primary, 0.9),
                },
              }}
            >
              Add Reseller
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        {[
          { label: 'TOTAL RESELLERS', value: stats.total, color: palette.primary },
          { label: 'ACTIVE', value: stats.active, color: palette.success },
          { label: 'SUSPENDED', value: stats.suspended, color: palette.warning },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: stat.color,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.extrabold,
                color: stat.color,
                lineHeight: 1,
                mt: 1,
              }}
            >
              {stat.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.15),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH RESELLERS..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="resellers"
          />
        </Box>
      </Card>

      {/* Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <GroupIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO RESELLERS
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Approved B2B resellers will appear here
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${colors.border}` } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.surfaceElevated, borderBottom: `2px solid ${palette.primary}` }}>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Company
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Contact
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Pricing Tier
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Active
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((reseller: ResellerProfile, index: number) => (
                    <TableRow
                      key={reseller.id}
                      sx={{
                        borderLeft: `4px solid ${reseller.isApproved ? palette.success : palette.warning}`,
                        ...animations.tableRowFade(index),
                        '&:hover': {
                          backgroundColor: alpha(palette.primary, 0.05),
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ fontSize: 18, color: palette.primary }} />
                          <Typography
                            sx={{
                              fontFamily: typography.fontFamily.display,
                              fontWeight: typography.fontWeight.semibold,
                              color: colors.textPrimary,
                            }}
                          >
                            {reseller.companyName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography sx={{ color: colors.textPrimary }}>
                            {reseller.user ? `${reseller.user.firstName} ${reseller.user.lastName}` : 'N/A'}
                          </Typography>
                          <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>
                            {reseller.user?.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {reseller.defaultTier ? (
                          <Chip
                            icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                            label={reseller.defaultTier.tierName.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: alpha(palette.info, 0.1),
                              color: palette.info,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: colors.textMuted }}>None</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={reseller.isApproved}
                          onChange={() => handleToggleApproved(reseller.id, reseller.isApproved)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: palette.success },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.success },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => onResellerClick ? onResellerClick(reseller) : navigate(`/app/resellers/${reseller.id}`)}
                              sx={{
                                border: `1px solid ${colors.border}`,
                                borderRadius: theme.radius.xs,
                                color: colors.textPrimary,
                                '&:hover': {
                                  borderColor: palette.primary,
                                  backgroundColor: alpha(palette.primary, 0.1),
                                  color: palette.primary,
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke Access">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(reseller.id)}
                              sx={{
                                border: `1px solid ${colors.border}`,
                                borderRadius: theme.radius.xs,
                                color: colors.textPrimary,
                                '&:hover': {
                                  borderColor: palette.error,
                                  backgroundColor: alpha(palette.error, 0.1),
                                  color: palette.error,
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ '& .MuiTablePagination-toolbar': { color: colors.textPrimary } }}
              />
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
